import {
  Component,
  ViewChild,
  ElementRef,
  NgZone,
  AfterViewInit,
  Input, OnInit,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  FormGroup, ValidationErrors,
  Validators,
} from '@angular/forms';
import { GooglePlacesService } from '../../services/google-places/google-places.service';
import { AddressForm } from '../../interfaces/address-form';

@Component({
  selector: 'app-address[formGroup]',
  templateUrl: './address.component.html',
  styleUrls: ['./address.component.scss'],
})
export class AddressComponent implements ControlValueAccessor, AfterViewInit, OnInit {
  @Input() public placeholder?: string;

  @Input() public formGroup: FormGroup<AddressForm>;

  @Input() public readonly: boolean = false;

  @ViewChild('search') public searchInput: ElementRef;

  public isManualEntry: boolean = false;

  public searchControl: FormControl;

  public states: { value: string }[] = [
    { value: 'ACT' },
    { value: 'NSW' },
    { value: 'NT' },
    { value: 'QLD' },
    { value: 'SA' },
    { value: 'TAS' },
    { value: 'VIC' },
    { value: 'WA' },
  ];

  autocomplete: google.maps.places.Autocomplete;

  private onChange: any = () => {};

  private onTouched: any = () => {};

  constructor(
    private ngZone: NgZone,
    private googlePlacesService: GooglePlacesService
  ) {}

  public ngAfterViewInit() {
    this.setupAutocomplete();
    if (this.readonly) {
      this.setManualEntryMode();
      this.formGroup.disable();
    }
  }

  public writeValue(val: any): void {
    this.formGroup.setValue(val, { emitEvent: false }); // Avoids unnecessary change detection cycles
  }

  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  public setManualEntryMode(): void {
    this.isManualEntry = true;
  }

  public setSearchMode(): void {
    this.formGroup.reset({
      streetAddress: '',
      suburb: '',
      state: '',
      postcode: '',
    });

    this.searchInput.nativeElement.value = '';
    this.isManualEntry = false;
  }

  setupAutocomplete(): void {
    this.autocomplete = new google.maps.places.Autocomplete(
      this.searchInput.nativeElement,
      {
        componentRestrictions: { country: 'au' },
      }
    );

    this.autocomplete.addListener('place_changed', () => this.onPlaceChanged());
  }

  /**
   * Callback function called when the place is changed in the autocomplete.
   * This function retrieves the selected place, transforms the address components,
   * and performs additional actions.
   *
   * @private
   * @returns {void}
   */
  onPlaceChanged(): void {
    this.ngZone.run(() => {
      const place: google.maps.places.PlaceResult = this.autocomplete.getPlace();
      /**
       * Checks if the geometry of the selected place is undefined or null.
       * If the geometry is undefined or null, it indicates that the selected
       * place does not have valid geometry information,
       * and therefore, the function returns early without further processing.
       */
      if (place.geometry === undefined || place.geometry === null) {
        return;
      }

      const transformedAddress = this.googlePlacesService.transformAddress(
        place.address_components ?? []
      );

      this.formGroup.setValue(transformedAddress);
      this.onChange(transformedAddress);
      this.setManualEntryMode();
    });
  }

  ngOnInit(): void {
    this.searchControl = new FormControl('', { validators: this.formGroup.controls.streetAddress.hasValidator(Validators.required) ? [Validators.required, this.addressRequiredValidator()] : [this.addressRequiredValidator()] });
  }

  addressRequiredValidator(): (control: AbstractControl) => ValidationErrors | null {
    return (control: AbstractControl): ValidationErrors | null => {
      const suburbControl = this.formGroup.controls.suburb;
      const streetAddressControl = this.formGroup.controls.streetAddress;
      const postcodeControl = this.formGroup.controls.postcode;
      const stateControl = this.formGroup.controls.state;

      const suburbRequired = suburbControl?.hasError('required') && !suburbControl?.value;
      const streetAddressRequired = streetAddressControl?.hasError('required') && !streetAddressControl?.value;
      const postcodeRequired = postcodeControl?.hasError('required') && !postcodeControl?.value;
      const stateRequired = stateControl?.hasError('required') && !stateControl?.value;

      if (suburbRequired || streetAddressRequired || postcodeRequired || stateRequired) {
        return { addressRequired: true };
      }

      return null;
    };
  }
}
